import React, { useState, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import TextInputField from '../../components/TextInputField';
import { authService } from '../../data';
import { getApi } from '../../utils/api';
import { log } from '../../utils';
import NavLogo from '../../assets/navLogo.png';
import { useAuth } from '../../App';
import {
  resolveUserStores,
  navigateForStores,
  withStores,
} from '../../utils/storeRouting';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  background-color: #F9F6F2;
`;

const LoginForm = styled.form`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h1`
  color: #4A3C31;
  text-align: center;
  margin-bottom: 30px;
`;

const VerificationMessage = styled.div`
  color: #4A3C31;
  text-align: center;
  margin: 40px 0;
  font-size: 16px;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  color: #FF6B6B;
  margin-bottom: 1rem;
  text-align: center;
`;

const Button = styled.button`
  background-color: #C5A572;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin: 20px auto 0 auto;
  display: block;
  font-size: 16px;
  &:hover {
    background-color: #E8D5B5;
  }
  &:disabled {
    background-color: #E8D5B5;
    cursor: not-allowed;
  }
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 18px auto;
  height: 100px;
`;

const Login = () => {
  const navigate = useNavigate();
  const { setIsAuthed, setUser, setStores } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Track if component is mounted
  const isMounted = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Set component header
  React.useEffect(() => {
    getApi().instance.defaults.headers['X-React-Component'] = 'Login';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Email not registered. Please reach out to service@tdpbakery.com');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const result = await authService.initiateLogin(email.trim().toLowerCase());
      log('Login attempt result', { result });
      
      if (result.success) {
        if (result.status === 'authenticated' || result.isAuthenticated) {
          log('User is authenticated, resolving stores from login response');
          setIsAuthed(true);

          let resolvedStores;
          try {
            resolvedStores = await resolveUserStores(result.user);
          } catch (storeError) {
            log('Store resolution error on login:', storeError);
            setError('Unable to load your stores. Please try again.');
            setIsSubmitting(false);
            return;
          }

          const hydratedUser = withStores(result.user, resolvedStores);
          setUser(hydratedUser);
          setStores(resolvedStores);
          log('Stores from login response', { stores: resolvedStores });
          startTransition(() => navigateForStores(navigate, resolvedStores));
          return;
        } else {
          // Magic link was sent
          setSuccess('Magic link sent! Please check your email to continue.');
        }
      } else {
        setError(result.message || 'Failed to send magic link');
      }
         } catch (error) {
       console.error('Login error:', error);
       // Handle specific HTTP status codes
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('No active user found with this email')) {
         // Check if it's an email not found error
           setError('Email not registered. Please reach out to service@tdpbakery.com to register the email');         
       } else if (error.response?.status === 404){
         setError('Site under maintenance. Please try again later.');
       } else {
         setError('An unexpected error occurred. Please try again.');
       }
     }
    
    setIsSubmitting(false);
  };

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <Logo src={NavLogo} alt="Logo" />
        <Title>Customer Portal</Title>
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}
        {success ? (
          <VerificationMessage>
            A verification link is sent. Please check your email to continue.
          </VerificationMessage>
        ) : (
          <>
            <TextInputField
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email"
              disabled={isSubmitting}
            />
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Please wait...' : 'Login'}
            </Button>
          </>
        )}
      </LoginForm>
    </LoginContainer>
  );
};

export default Login;
